import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "backdrop", "panel", "date", "mealType", "mealTypeId", "list", "searchInput", "dateHidden", "mealTypeHidden"]
  static values = { recipes: Array, mealTypes: Object }

  connect() {
    const recipesEl = document.getElementById("recipes-data")
    const mealTypesEl = document.getElementById("meal-types-data")
    if (recipesEl) this.recipesValue = JSON.parse(recipesEl.textContent)
    if (mealTypesEl) this.mealTypesValue = JSON.parse(mealTypesEl.textContent)
  }

  open(event) {
    const { date, dateDisplay, mealTypeId, mealTypeName } = event.currentTarget.dataset

    this.dateTarget.textContent = dateDisplay
    this.mealTypeTarget.textContent = mealTypeName
    this.dateHiddenTarget.value = date
    this.mealTypeHiddenTarget.value = mealTypeId
    this.currentDate = date
    this.currentMealTypeId = mealTypeId

    this.renderRecipes()
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  close() {
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    if (this.searchInputTarget) this.searchInputTarget.value = ""
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  filter() {
    const query = this.normalize(this.searchInputTarget.value.toLowerCase().trim())
    const items = this.listTarget.querySelectorAll("[data-recipe-name]")

    items.forEach(item => {
      const name = this.normalize(item.dataset.recipeName)
      if (query === "" || name.includes(query)) {
        item.style.display = ""
      } else {
        item.style.display = "none"
      }
    })
  }

  normalize(str) {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  }

  renderRecipes() {
    this.listTarget.innerHTML = this.recipesValue.map(recipe => {
      const details = []
      if (recipe.total_time_minutes) details.push(`${recipe.total_time_minutes} min`)
      if (recipe.servings) details.push(`${recipe.servings} porcs`)
      if (recipe.difficulty) details.push(`<span class="badge badge-${recipe.difficulty} scale-75 origin-left">${recipe.difficulty}</span>`)

      return `
        <button type="button"
          class="w-full text-left p-3 bg-charcoal hover:bg-charcoal-light border-l-2 border-transparent hover:border-terracotta transition-colors group cursor-pointer"
          data-recipe-name="${recipe.name.toLowerCase()}"
          data-recipe-id="${recipe.id}"
          data-action="click->recipe-picker#selectRecipe">
          <div class="font-medium text-cream group-hover:text-terracotta transition-colors">
            ${recipe.name}
          </div>
          ${details.length > 0 ? `
            <div class="flex items-center gap-3 mt-1 font-mono text-[10px] text-cream/40">
              ${details.join('<span class="text-cream/20">|</span>')}
            </div>
          ` : ''}
        </button>
      `
    }).join("")
  }

  async selectRecipe(event) {
    const recipeId = event.currentTarget.dataset.recipeId
    const recipeName = event.currentTarget.querySelector(".font-medium").textContent.trim()

    this.showOptimisticUpdate(recipeName)
    this.close()

    await this.submitMealPlan({ recipe_id: recipeId })
  }

  isToday(dateStr) {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')
    return dateStr === `${year}-${month}-${day}`
  }

  showOptimisticUpdate(recipeName) {
    const slotId = `meal_slot_${this.currentDate}_${this.currentMealTypeId}`
    const slot = document.getElementById(slotId)
    if (!slot) return

    const innerDiv = slot.querySelector("div")
    if (innerDiv) {
      const bg = this.isToday(this.currentDate) ? 'bg-terracotta/10' : 'bg-charcoal'
      innerDiv.className = `min-h-[100px] p-2 group relative ${bg}`
      innerDiv.innerHTML = `
        <div class="flex-1 group/card relative p-1 -m-1 rounded">
          <div class="font-medium text-sm text-cream/50 truncate animate-pulse">
            ${recipeName}
          </div>
          <div class="flex items-center gap-2 mt-1 font-mono text-[10px] text-cream/20">
            <span>...</span>
          </div>
        </div>
      `
    }
  }

  async deleteMealPlan(event) {
    event.stopPropagation()
    const { mealPlanId, date, dateDisplay, mealTypeId, mealTypeName } = event.currentTarget.dataset

    this.showOptimisticDelete(date, dateDisplay, mealTypeId, mealTypeName)

    const csrfToken = document.querySelector("meta[name='csrf-token']").content
    await fetch(`/meal_plans/${mealPlanId}`, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      }
    })
  }

  showOptimisticDelete(date, dateDisplay, mealTypeId, mealTypeName) {
    const slotId = `meal_slot_${date}_${mealTypeId}`
    const slot = document.getElementById(slotId)
    if (!slot) return

    const innerDiv = slot.querySelector("div")
    if (innerDiv) {
      const bg = this.isToday(date) ? 'bg-terracotta/10' : 'bg-charcoal'
      innerDiv.className = `min-h-[100px] p-2 group relative ${bg}`
      innerDiv.innerHTML = `
        <button type="button"
          data-action="click->recipe-picker#open"
          data-date="${date}"
          data-date-display="${dateDisplay}"
          data-meal-type-id="${mealTypeId}"
          data-meal-type-name="${mealTypeName}"
          class="w-full h-full flex-1 flex items-center justify-center min-h-[60px] border border-dashed border-cream/10 hover:border-terracotta/50 hover:bg-terracotta/5 transition-colors rounded cursor-pointer group/add bg-transparent">
          <div class="flex items-center gap-2 text-cream/20 group-hover/add:text-terracotta/60 transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
            </svg>
            <span class="font-mono text-xs">Agregar</span>
          </div>
        </button>
      `
    }
  }

  async submitNote(event) {
    event.preventDefault()
    const form = event.currentTarget
    const notes = form.querySelector("input[name='meal_plan[notes]']").value
    if (!notes.trim()) return
    await this.submitMealPlan({ notes })
    form.reset()
  }

  async submitMealPlan(extraParams) {
    const csrfToken = document.querySelector("meta[name='csrf-token']").content
    const params = new URLSearchParams({
      "meal_plan[date]": this.currentDate,
      "meal_plan[meal_type_id]": this.currentMealTypeId,
      ...Object.fromEntries(Object.entries(extraParams).map(([k, v]) => [`meal_plan[${k}]`, v]))
    })

    const response = await fetch("/meal_plans", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: params
    })

    if (response.ok) {
      const html = await response.text()
      Turbo.renderStreamMessage(html)
      this.close()
    }
  }
}
